import { useEffect, useState } from 'react';

import { IconInfoCircleOutline } from '@iwoca/icons';
import { Label, Link, Pill, TextArea } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyQuestionAI.module.css';
import { useCompanyQuestionMutation } from '../../api/apiCompanyQuestion/apiCompanyQuestionMutation';
import { useGetCompanyQuestion } from '../../api/apiCompanyQuestion/apiCompanyQuestionQuery';
import {
  TCompanyQuestionRequest,
  TSicCode,
} from '../../api/apiCompanyQuestion/fetchPostCompanyQuestion';
import {
  useGetApplicationQuery,
  useGetStateQuery,
} from '../../api/lendingApi/lendingApiQueries';
import { useOnce } from '../../hooks/useOnce';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { TGetStateResponse } from '../../types';
import { Button } from '../Button/Button';
import RouteSpinner from '../RouteSpinner/RouteSpinner';

type TCompanyQuestionAIData = {
  answer: string;
  sicCode: string[];
};

type TQuestionHistory = {
  id: number;
  question: string;
  answer: string;
};

type TSessionData = {
  questions: TQuestionHistory[];
  nextQuestion: string;
};

const getSessionStorageKey = (applicationId: string) =>
  `company_questions_${applicationId}`;

const getSessionData = (applicationId?: string): TSessionData => {
  if (!applicationId) return { questions: [], nextQuestion: '' };

  try {
    const data = sessionStorage.getItem(getSessionStorageKey(applicationId));
    return data ? JSON.parse(data) : { questions: [], nextQuestion: '' };
  } catch {
    return { questions: [], nextQuestion: '' };
  }
};

const getSicCodesSessionData = (companyHouseNumber: string): TSicCode => {
  try {
    const data = sessionStorage.getItem(
      `sic_codes_for_company_${companyHouseNumber}`,
    );

    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const setSessionData = (applicationId: string, data: TSessionData): void => {
  sessionStorage.setItem(
    getSessionStorageKey(applicationId),
    JSON.stringify(data),
  );
};

const normaliseCompanyQuestion = (
  data: TCompanyQuestionAIData,
  history: TQuestionHistory[],
  sicCodes?: string[],
): TCompanyQuestionRequest => {
  return {
    context: {
      sic_code: sicCodes || [],
    },
    history,
  };
};

const CompanyQuestionAI = () => {
  const [textCount, setTextCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const [sicCodes, setSicCodes] = useState<string[]>([]);
  const methods = useForm<TCompanyQuestionAIData>();
  const { application } = useGetApplicationQuery();
  const [questionStorage, setQuestionStorage] = useState<TSessionData>(
    getSessionData(application?.application_id),
  );
  const [sessionStorageLoaded, setSessionStorageLoaded] = useState(false);

  const { initialQuestion, isInitialCompanyQuestionLoading } =
    useGetCompanyQuestion({
      enabled: sessionStorageLoaded && questionStorage?.questions.length === 0,
    });
  const { postCompanyQuestion, isLoading } = useCompanyQuestionMutation();

  const { state } = useGetStateQuery<TGetStateResponse>();

  const { navigateSignup } = useSignupRouterUK();

  const { handleSubmit, register, reset } = methods;

  const maxTextLength = 500;

  useOnce({
    fn: () => {
      if (!application?.application_id) return;

      const sessionData = getSessionData(application.application_id);
      setQuestionStorage(sessionData);

      if (!state.application?.company?.company_number) return;
      const sessionSicCodes = getSicCodesSessionData(
        state.application?.company?.company_number,
      );

      setSicCodes(sessionSicCodes || []);

      setSessionStorageLoaded(Boolean(sessionData));

      if (sessionData.questions.length >= 5) {
        setShowThankYou(true);
      }
    },
    conditionFn: Boolean(application?.application_id),
  });

  useEffect(() => {
    if (!sessionStorageLoaded) return;

    if (questionStorage.nextQuestion) {
      setCurrentQuestion(questionStorage.nextQuestion);
    } else if (initialQuestion && questionStorage.questions.length === 0) {
      setCurrentQuestion(initialQuestion);
    } else if (!initialQuestion && questionStorage.questions.length === 0) {
      setCurrentQuestion(
        'What products or services does your business provide, and who are your primary customers?',
      );
    }
  }, [initialQuestion, questionStorage, sessionStorageLoaded]);

  const onSubmit = async (data: TCompanyQuestionAIData) => {
    if (!application?.application_id) {
      return;
    }

    const updatedQuestionCount = questionStorage.questions.length + 1;

    try {
      const newQuestionEntry: TQuestionHistory = {
        id: updatedQuestionCount,
        question: currentQuestion,
        answer: data.answer,
      };

      const updatedHistory = [...questionStorage.questions, newQuestionEntry];

      const response = await postCompanyQuestion({
        applicationId: application.application_id,
        data: normaliseCompanyQuestion(data, updatedHistory, sicCodes),
      });

      if (!response) throw new Error('No questions populated');

      setSessionData(application.application_id, {
        questions: updatedHistory,
        nextQuestion: response.inference_question || '',
      });
      setQuestionStorage({
        questions: updatedHistory,
        nextQuestion: response.inference_question || '',
      });

      const shouldShowThankYou =
        response.questions_complete || updatedQuestionCount >= 5;

      if (shouldShowThankYou) {
        setShowThankYou(true);
      } else if (response.inference_question) {
        setCurrentQuestion(response.inference_question);
        reset();
        setTextCount(0);
      }
    } catch (e) {
      throw new Error('Failed fetch', e as Error);
    }
  };

  if (isInitialCompanyQuestionLoading) {
    return <RouteSpinner />;
  }

  return (
    <>
      <div className={styles.container}>
        <Pill>Powered by AI</Pill>
        <h2 className={styles.heading}>
          Tell us a bit more about your business
        </h2>
        <p className={styles.description}>
          This will help us understand your business better and more accurately
          assess your application.
        </p>
        <div className={styles.info}>
          <IconInfoCircleOutline
            data-testid="infoIcon"
            width={16}
            height={16}
            className={styles.infoIcon}
          />
          <p className={styles.infoDescription}>
            There are a maximum of five AI generated questions and they should
            take around two minutes to complete.
          </p>
        </div>

        {questionStorage.questions.length > 0 && (
          <div className={styles.previousQuestions}>
            {questionStorage.questions.map((history) => (
              <div key={history.id} className={styles.previousQuestion}>
                <p className={styles.previousQuestion}>{history.question}</p>
                <p className={styles.previousAnswer}>{history.answer}</p>
              </div>
            ))}
          </div>
        )}

        {!showThankYou && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
              <div className={styles.formFieldContainer}>
                <div className={styles.formField}>
                  <Label htmlFor="questionAndAnswer">{currentQuestion}</Label>
                  <TextArea
                    data-testid={'answer-textarea'}
                    id="questionAndAnswer"
                    placeholder="Type your answer here..."
                    maxLength={maxTextLength}
                    className={styles.questionTextArea}
                    {...register('answer', { required: true })}
                    onChange={(e) => setTextCount(e.currentTarget.value.length)}
                  />
                </div>
                <p
                  data-testid="character-count"
                  className={styles.characterCount}
                >
                  {textCount} / {maxTextLength} characters
                </p>
              </div>
              <Button
                className={styles.nextQuestionBtn}
                type="submit"
                disabled={isLoading}
                loading={isLoading}
              >
                Continue
              </Button>
              {questionStorage.questions.length < 1 && (
                <p
                  data-testid="ai-privacy-policy"
                  className={styles.privacyPolicy}
                >
                  By clicking “Continue” you agree to our{' '}
                  <Link
                    href="https://www.iwoca.co.uk/compliance/customer-privacy-policy"
                    className={styles.privacyPolicyLink}
                  >
                    privacy policy
                  </Link>
                  .
                </p>
              )}
            </form>
          </FormProvider>
        )}
        {showThankYou && (
          <>
            <p className={styles.thankYouMessage}>
              Thanks for that information. You can now continue to the next step
              of your application.
            </p>

            <Button
              className={styles.nextQuestionBtn}
              onClick={() => navigateSignup({ route: 'check-eligibility' })}
            >
              Continue to check eligibility
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export default CompanyQuestionAI;
